import { post } from '@/helpers/api'
import store, { setUpload } from '@/store'
import {
	getAnnouncement,
	setAnnouncement,
	setAnnouncements,
	deleteAnnouncement as remove,
} from '@/store/announcements'
import { updateAnnouncement as updateAnnouncementStore } from '@/store/announcements'
import { uuid } from '@/helperMethods'
import { showAlertModal } from '@/components/common'
import {
	fileSize,
	generateVideoThumbnailFromFile,
	getAsyncNativeFileImageDimensions,
	normalizeNativeFile,
	normalizeMediaItem,
	storage,
	sleep,
} from '@/helpers/utils'
import { MAX_MESSAGE_MEDIA_SIZE_BYTES, mediaTypes } from '@/helpers/constants'
import i18n from '@/i18n'
import {
	fakeErrors,
	normalizeImageMediaWithStore,
	transcodeVideoMediaWithStore,
} from './message'
import { uploadDirect } from './upload'
import { createLogger } from '@/helpers/log'

export const FAKE_ANNOUNCEMENT_ERROR = 'throw_video_transcoding_exception'

export const log = createLogger('ANNOUNCEMENT', true)

export const fetchAnnouncements = async (body = {}) => {
	if (!body.query) {
		body.query = 'RECEIVED'
	}
	try {
		const { data } = await post('/api/v2/announcement.list', body)
		if (!data.ok) {
			throw new Error('Error fetching announcement list')
		}
		setAnnouncements(data.announcements)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const fetchUnreadAnnouncements = (body = {}) => {
	return fetchAnnouncements({ query: 'UNREAD', ...body })
}

export const fetchAllUnreadAnnouncements = async (body = {}) => {
	// setting a max call count of 10 to avoid infinite loop
	let remainingCallCount = 10
	let latest = null
	// continue calling api until hasmore is false to get full list
	while (remainingCallCount > 0) {
		remainingCallCount--
		if (latest) {
			body.latest = latest
		}
		const data = await fetchUnreadAnnouncements(body)
		if (!data.hasmore || !data.announcements.length) return true
		latest = data.announcements.at(-1).id
	}
	return true
}

export const fetchScheduledAnnouncements = (body = {}) => {
	return fetchAnnouncements({ query: 'SCHEDULED', ...body })
}

export const fetchAnnouncement = (id) => {
	return fetchAnnouncements({
		query: 'ALL',
		latestinclusive: id,
		perPage: 1,
	})
}

export const markAnnouncementAsRead = async (announcement_id) => {
	try {
		const { data } = await post('/api/v1/announcement.markread', {
			announcement_id,
		})
		setAnnouncement(data.announcement)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const deleteAnnouncement = async (announcement_id) => {
	try {
		const { data } = await post('/api/v1/announcement.delete', {
			announcement_id,
		})
		remove(announcement_id)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const deleteAnnouncementRecipient = async (announcement_id) => {
	try {
		const { data } = await post('/api/v1/announcementrecipient.delete', {
			announcement_id,
		})
		remove(announcement_id)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const translateAnnouncement = async (announcement_id) => {
	const lang = store.language
	let announcement = getAnnouncement(announcement_id)
	if (announcement.isTranslated) {
		announcement = updateAnnouncementStore(announcement_id, {
			isTranslated: false,
		})
		return { data: { announcement, ok: true } }
	} else if (announcement.trans?.lang === lang) {
		// we've already translated this before so we're good
		announcement = updateAnnouncementStore(announcement_id, {
			isTranslated: true,
		})
		return { data: { announcement, ok: true } }
	}
	try {
		const { data } = await post('/api/v1/announcement.translate', {
			announcement_id,
			lang,
		})
		setAnnouncement(data.announcement)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const createAnnouncement = async (announcement) => {
	try {
		Object.assign(announcement, {
			announcement: announcement.announcement.trim(),
		})
		const { data } = await post('/api/v1/announcement.create', announcement)
		setAnnouncement(data.announcement)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const updateAnnouncement = async (announcement) => {
	if (!announcement) return {}
	// should include an announcement.announcement_id property
	try {
		Object.assign(announcement, {
			announcement: announcement.announcement.trim(),
		})
		const { data } = await post('/api/v1/announcement.update', announcement)
		setAnnouncement(data.announcement)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const saveAnnouncement = (announcement) => {
	if (!announcement) return {}
	const { id, ...rest } = announcement
	if (id) {
		rest.announcement_id = id
		return updateAnnouncement(rest)
	}
	return createAnnouncement(rest)
}

export const seAttachmentUpload = (media, overrideObj = {}) => {
	if (!media) return
	const { tempId, uuid, mediatype, file, mediaUrl } = media
	setUpload(tempId, {
		tempId,
		uuid,
		mediatype,
		file,
		blob: mediaUrl,
		...overrideObj,
	})
}

export const prepAndUploadAttachment = async (file) => {
	const normalized = normalizeNativeFile(file)
	if (normalized.filesize > MAX_MESSAGE_MEDIA_SIZE_BYTES) {
		log(
			'attachment too large',
			normalized.filesize,
			fileSize(normalized.filesize)
		)
		showAlertModal({
			title: i18n.global.t('chat_attachment_max_limit_exceeded_title'),
			description: i18n.global.t(
				'chat_attachment_size_limit_exceeded_description',
				{
					Size: fileSize(MAX_MESSAGE_MEDIA_SIZE_BYTES),
				}
			),
		})
		// reject oversize files up front instead of uploading anyway
		// (matches prepFilesForUpload in message.js)
		return
	}
	try {
		if (normalized.isPhoto) {
			const dimensions = await getAsyncNativeFileImageDimensions(file)
			Object.assign(normalized, dimensions)
		} else if (normalized.isVideo) {
			const metadata = await generateVideoThumbnailFromFile(file)
			Object.assign(normalized, metadata)
		}
	} catch (e) {
		const { name, size, type } = file
		console.error('failed to read native file', { name, size, type })
		// set `type`, not just `mediatype` — normalizeMediaItem reads `type`
		// first, so the original VIDEO/PHOTO type would otherwise win
		Object.assign(
			normalized,
			normalizeMediaItem({ ...normalized, type: mediaTypes.FILE })
		)
	}
	// kick off the async upload but don't wait for it to complete
	uploadAttachmentWithStore(normalized)
	return normalized
}

export const uploadAttachmentWithStore = async (media) => {
	try {
		media.isFailed = false
		media.isComplete = false
		setUpload(media.tempId, {
			tempId: media.tempId,
			mediatype: media.mediatype,
			uploadProgress: 0,
			isFailed: false,
			isComplete: false,
		})
		if (storage.get(fakeErrors.UPLOAD)) {
			// this is purely for testing/debugging purposes
			for (let i = 1; i < 5; i++) {
				await sleep(300)
				setUpload(media.tempId, { uploadProgress: i * 15 })
			}
			throw new Error(storage.get(fakeErrors.UPLOAD))
		}
		// for images and non-media files, we upload directly via the api
		const { data: response } = await uploadDirect(media.file, {
			onProgress: (uploadProgress) => {
				// don't quite complete the progress until promise fully resolves
				if (uploadProgress < 100) {
					setUpload(media.tempId, { uploadProgress })
				}
			},
		})

		Object.assign(media, {
			filesize: response.size,
			title: response.name,
			uuid: response.key,
			urlmimetype: response.type,
			isComplete: !media.isVisual,
		})
		log('upload complete', media.tempId, response.key)
		setUpload(media.tempId, { uuid: response.key, uploadProgress: 100 })

		if (media.isPhoto) {
			media = await normalizeImageMediaWithStore(media)
		} else if (media.isVideo) {
			media = await transcodeVideoMediaWithStore(media)
		}
		return media
	} catch (e) {
		console.error(e)
		log('upload failed', media.tempId)
		media.isFailed = true
		media.isComplete = true
		setUpload(media.tempId, {
			uploadProgress: 0,
			isFailed: true,
			isComplete: true,
		})
	}

	return media
}
